import i18n from '@/utils/i18n';
import {
  AlertLevel,
  CartUpdateError,
  SaveAndValidateSalesPortalCart_AlertFieldsFragment,
  SaveAndValidateSalesPortalCart_OrderValidationFieldsFragment,
  SaveSalesPortalCart_CartItemFieldsFragment,
  SaveSalesPortalCartMutation,
} from '@/types';
import { captureException } from '@/utils/sentry';
import {
  SavedCartItem,
  useCartStore,
} from '@/experiences/sales-portal/domain/ordering/blocs/stores/cart-store';
import { SavedSelectedFulfillment } from '@/experiences/sales-portal/domain/ordering/blocs/stores/selected-fulfillment-store';
import { SaveSalesPortalCartError } from '@/experiences/sales-portal/domain/ordering/blocs/errors/save-sales-portal-cart-error';
import { useCallback, useMemo } from 'react';
import { useExperienceToasts } from '@experiences/hooks/use-experience-toasts';
import { useSaveAndValidateSalesPortalCartMutation } from '@/experiences/sales-portal/domain/ordering/blocs/documents/save-and-validate-sales-portal-cart.generated';
import { useSaveSalesPortalCartMutation } from '@/experiences/sales-portal/domain/ordering/blocs/documents/save-sales-portal-cart.generated';

type SaveSalesPortalCartResult =
  SaveSalesPortalCartMutation['saveSalesPortalCart'];

const ALERT_LEVEL_PRIORITY: Record<AlertLevel, number> = {
  [AlertLevel.Negative]: 0,
  [AlertLevel.Warning]: 1,
  [AlertLevel.Info]: 2,
  [AlertLevel.Positive]: 3,
};

export interface OrderValidationAlert {
  level: AlertLevel;
  message: string;
}

export interface OrderValidation {
  alerts: OrderValidationAlert[] | null;
  canPlaceOrder: boolean;
}

/**
 * Transforms a SavedCartItem (client entity) into a SalesPortalCartItemInput (backend entity)
 *
 * @param {SavedCartItem} cartItem - The SavedCartItem to transform
 *
 * @returns The transformed SalesPortalCartItemInput
 *
 * @example
 * ```typescript
 * { cart_items: cart.cartItems.map(salesPortalCartItemInputTransformer) }
 * ```
 */
const salesPortalCartItemInputTransformer = (cartItem: SavedCartItem) => ({
  order_item_id: cartItem.orderItemUUID,
  quantity: cartItem.quantity,
  variant_pack_id: cartItem.variantPackUUID,
});

/**
 * Transforms a SavedCart (client entity) into a SalesPortalCartInput (backend entity)
 *
 * @param {object} props - The props for the transformer
 * @param {SavedCartItem[]} props.cartItems - The cart items to transform
 * @param {string | null} props.orderUUID - The order UUID
 * @param {string} props.restaurantUUID - The UUID of the restaurant
 * @param {string} props.supplierUUID - The UUID of the supplier
 * @param {SavedSelectedFulfillment} props.selectedFulfillment - The selected fulfillment to transform
 * @param {string} props.updatedAt - The last updated at timestamp
 *
 * @returns The transformed SalesPortalCartItemInput
 *
 * @example
 * ```typescript
 * const cartInput = salesPortalCartInputTransformer({
 *   restaurantUUID,
 *   supplierUUID,
 *   cartItems,
 *   selectedFulfillment,
 *   orderUUID,
 *   updatedAt,
 * });
 * const { data } = await saveSalesPortalCartMutation({ variables: { cart: cartInput }});
 * ```
 */
const salesPortalCartInputTransformer = ({
  restaurantUUID,
  supplierUUID,
  cartItems,
  selectedFulfillment,
  updatedAt,
  orderUUID,
  deliveryDate,
}: {
  cartItems: SavedCartItem[];
  deliveryDate: string;
  orderUUID: string | null;
  restaurantUUID: string;
  selectedFulfillment: SavedSelectedFulfillment;
  supplierUUID: string;
  updatedAt: string;
}) => ({
  cart_items: cartItems.map(salesPortalCartItemInputTransformer),
  metadata: {
    // fulfillment settings
    delivery_date: deliveryDate,
    fulfillment_timeslot_end: selectedFulfillment.timeslot?.endTime,
    fulfillment_timeslot_start: selectedFulfillment.timeslot?.startTime,
    fulfillment_type: selectedFulfillment.type,

    // order information
    order_id: orderUUID,
    restaurant_id: restaurantUUID,
    supplier_id: supplierUUID,
    updated_at: updatedAt,
  },
});

/**
 * Transforms a SalesPortalCartItemOutput (backend entity) into a SavedCartItem (client entity)
 *
 * @param {SaveSalesPortalCart_CartItemFieldsFragment} cartItem - The cart item to transform
 *
 * @returns The transformed SavedCartItem
 *
 * @example
 * ```typescript
 * const savedCartItems = data.saveSalesPortalCart.cart.cart_items.map(savedCartItemTransformer);
 * useCartStore.getState().setCart(restaurantUUID, { cartItems: savedCartItems });
 * ```
 */
const savedCartItemTransformer = (
  cartItem: SaveSalesPortalCart_CartItemFieldsFragment,
) => ({
  addedAt: cartItem.added_at ?? null,
  orderItemUUID: cartItem.order_item_id,
  quantity: cartItem.quantity,
  variantPackUUID: cartItem.variant_pack_id,
});

/**
 * Transforms a SalesPortalValidationAlert[] (backend entity) into an OrderValidationAlert[] (client entity)
 *   and sorts them by alert level in descending order
 *
 * @param alerts {SaveAndValidateSalesPortalCart_AlertFieldsFragment[] | null | undefined} - The alerts to transform
 *
 * @returns The transformed OrderValidationAlert
 *
 * @example
 * ```typescript
 * const orderValidationAlerts = orderValidationAlertsTransformer(data.saveSalesPortalCart.order_validation.alerts);
 * ```
 */
const orderValidationAlertsTransformer = (
  alerts:
    | SaveAndValidateSalesPortalCart_AlertFieldsFragment[]
    | null
    | undefined,
): OrderValidationAlert[] | null => {
  if (!alerts || alerts.length === 0) {
    return null;
  }

  return alerts
    .sort(
      (a, b) =>
        ALERT_LEVEL_PRIORITY[a.alert_level] -
        ALERT_LEVEL_PRIORITY[b.alert_level],
    )
    .map((alert) => ({
      level: alert.alert_level,
      message: alert.alert_message,
    }));
};

/**
 * Transforms a SalesPortalCartValidation (backend entity) into an OrderValidation (client entity)
 *
 * @param orderValidation {SalesPortalCartValidation | null} - The SalesPortalCartValidation to transform
 *
 * @returns The transformed OrderValidation
 *
 * @example
 * ```typescript
 * const orderValidation = orderValidationTransformer(data.saveSalesPortalCart.order_validation);
 * ```
 */
const orderValidationTransformer = (
  orderValidation?: SaveAndValidateSalesPortalCart_OrderValidationFieldsFragment | null,
): OrderValidation | undefined => {
  if (!orderValidation) {
    return undefined;
  }

  return {
    alerts: orderValidationAlertsTransformer(orderValidation.alerts) ?? null,
    canPlaceOrder: orderValidation?.can_place_order,
  };
};

/**
 * Syncs the local cart with the remote cart.
 *
 * @param restaurantUUID {string} - The UUID of the restaurant
 * @param remoteCart {SaveSalesPortalCartResult['cart']} - The remote cart
 * @param selectedFulfillment {SavedSelectedFulfillment} - The selected fulfillment
 * @param supplierTimezone {string} - The supplier's timezone
 * @param supplierUUID {string} - The UUID of the supplier
 *
 * @example
 * ```typescript
 * const restaurantUUID = '123';
 * const remoteCart = data.saveSalesPortalCart.cart;
 * syncSavedCartWithRemoteCart(restaurantUUID, remoteCart);
 * ```
 */
const syncSavedCartWithRemoteCart = (
  restaurantUUID: string,
  remoteCart: SaveSalesPortalCartResult['cart'],
  selectedFulfillment: SavedSelectedFulfillment,
  supplierTimezone: string,
  supplierUUID: string,
) => {
  if (!remoteCart) {
    return;
  }

  const savedCartItems = remoteCart.cart_items.map(savedCartItemTransformer);

  useCartStore.getState().setCart({
    cartItems: savedCartItems,
    fulfillment: selectedFulfillment,
    lastSavedAt: remoteCart.metadata.updated_at,
    lastUpdatedAt: remoteCart.metadata.updated_at,
    orderUUID: remoteCart.metadata.order_id ?? null,
    restaurantUUID,
    supplierTimezone,
    supplierUUID,
  });
};

/**
 * Handles the response from the save sales portal cart mutation.
 *
 * @param data {SaveSalesPortalCartResult} - The response from the save sales portal cart mutation
 * @param restaurantUUID {string} - The UUID of the restaurant
 * @param selectedFulfillment {SavedSelectedFulfillment} - The selected fulfillment
 * @param supplierTimezone {string} - The supplier's timezone
 * @param supplierUUID {string} - The UUID of the supplier
 */
function handleSaveSalesPortalCartResponse(
  response: SaveSalesPortalCartResult | null,
  restaurantUUID: string,
  selectedFulfillment: SavedSelectedFulfillment,
  supplierTimezone: string,
  supplierUUID: string,
) {
  // if active restaurant uuid differs from the provided restaurant uuid;
  // prevent corrupting saved cart
  const activeRestaurantUUID = useCartStore
    .getState()
    .getCart()?.restaurantUUID;
  if (activeRestaurantUUID !== restaurantUUID) {
    return;
  }

  if (!response) {
    throw new SaveSalesPortalCartError({
      code: CartUpdateError.UnexpectedException,
      message: i18n.t('sales_portal.order_entry.save_cart_error.unknown_error'),
    });
  }

  // in-memory cart might have changed since we made the request
  const currentLocalUpdatedAt = Date.parse(
    useCartStore.getState().getCart()?.lastUpdatedAt ?? '',
  );
  const currentRemoteUpdatedAt = Date.parse(
    response?.cart?.metadata?.updated_at ?? '',
  );

  // if save was successful, sync local with remote
  if (response.success) {
    // if remote cart is newer, sync local with remote
    if (currentRemoteUpdatedAt >= currentLocalUpdatedAt) {
      syncSavedCartWithRemoteCart(
        restaurantUUID,
        response.cart,
        selectedFulfillment,
        supplierTimezone,
        supplierUUID,
      );
    }
  } else if (response.error) {
    syncSavedCartWithRemoteCart(
      restaurantUUID,
      response.cart,
      selectedFulfillment,
      supplierTimezone,
      supplierUUID,
    );

    throw new SaveSalesPortalCartError({
      code: response.error.code,
      message: response.error.message,
    });
  } else {
    throw new SaveSalesPortalCartError({
      code: CartUpdateError.UnexpectedException,
      message: i18n.t('sales_portal.order_entry.save_cart_error.unknown_error'),
    });
  }
}

/**
 * Drops the failed save's order id so the next save resolves the draft by
 * supplier and restaurant on the server
 */
const dropFailedOrderUUID = (
  failedOrderUUID: string | null,
  restaurantUUID: string,
): void => {
  const currentCart = useCartStore.getState().getCart();
  if (
    !currentCart ||
    currentCart.restaurantUUID !== restaurantUUID ||
    currentCart.orderUUID !== failedOrderUUID ||
    currentCart.orderUUID === null
  ) {
    return;
  }
  useCartStore.getState().setCart({ ...currentCart, orderUUID: null });
};

/**
 * Mutator to save the in-memory cart.
 * Reads cart metadata (supplierUUID, restaurantUUID, supplierTimezone) from the cart store.
 * Errors are handled internally and displayed via toast.
 *
 * @returns {Promise<boolean>} true if save succeeded, false if it failed
 *
 * @example
 * ```typescript
 * const saveCart = useSaveSalesPortalCartMutator();
 * const success = await saveCart();
 * if (success) {
 *   // proceed with next steps
 * }
 * ```
 */
export const useSaveSalesPortalCartMutator = () => {
  // apollo cache will block main thread (locking up the UI) and we don't need caching for saving the cart
  const [saveSalesPortalCartMutation] = useSaveSalesPortalCartMutation({
    context: { noBatch: true },
    fetchPolicy: 'no-cache',
  });
  const { showErrorToast } = useExperienceToasts();

  return useCallback(async (): Promise<boolean> => {
    // get current in-memory cart
    const currentCart = useCartStore.getState().getCart();

    if (!currentCart) {
      showErrorToast(
        i18n.t('sales_portal.order_entry.save_cart_error.missing_cart'),
      );
      return false;
    }

    const {
      supplierUUID,
      restaurantUUID,
      supplierTimezone,
      fulfillment: selectedFulfillment,
    } = currentCart;

    if (!selectedFulfillment) {
      showErrorToast(
        i18n.t('sales_portal.order_entry.save_cart_error.missing_fulfillment'),
      );
      return false;
    }

    const lastUpdatedAt = currentCart.lastUpdatedAt ?? new Date().toISOString(); // default to current timestamp
    const cartItems = currentCart.cartItems ?? [];
    const orderUUID = currentCart.orderUUID ?? null;

    // transform cart items into save cart input
    const setCartInput = salesPortalCartInputTransformer({
      cartItems,
      deliveryDate: selectedFulfillment.date,
      orderUUID,
      restaurantUUID,
      selectedFulfillment,
      supplierUUID,
      updatedAt: lastUpdatedAt,
    });

    try {
      // save cart
      const { data } = await saveSalesPortalCartMutation({
        variables: { cart: setCartInput },
      });

      handleSaveSalesPortalCartResponse(
        data?.saveSalesPortalCart ?? null,
        restaurantUUID,
        selectedFulfillment,
        supplierTimezone,
        supplierUUID,
      );
      return true;
    } catch (error) {
      captureException(error, {
        extra: {
          mutation: 'saveSalesPortalCart',
          orderUUID,
          restaurantUUID,
          supplierUUID,
        },
      });
      showErrorToast(
        i18n.t('sales_portal.order_entry.save_cart_error.unknown_error'),
      );
      dropFailedOrderUUID(orderUUID, restaurantUUID);
      return false;
    }
  }, [saveSalesPortalCartMutation, showErrorToast]);
};

/**
 * Mutator to save the selected fulfillment on a draft order.
 * Reads cart metadata (supplierUUID, restaurantUUID, supplierTimezone) from the cart store.
 * Errors are handled internally and displayed via toast.
 *
 * @returns {Promise<boolean>} true if save succeeded, false if it failed
 *
 * @example
 * ```typescript
 * const saveCartFulfillmentMutator = useSaveSalesPortalCartFulfillmentMutator();
 * const success = await saveCartFulfillmentMutator(selectedFulfillment);
 * if (success) {
 *   // proceed with next steps
 * }
 * ```
 */
export const useSaveSalesPortalCartFulfillmentMutator = () => {
  const [saveSalesPortalCartMutation] = useSaveSalesPortalCartMutation({
    context: { noBatch: true },
    fetchPolicy: 'no-cache',
  });
  const { showErrorToast } = useExperienceToasts();

  return useCallback(
    async (selectedFulfillment: SavedSelectedFulfillment): Promise<boolean> => {
      const currentCart = useCartStore.getState().getCart();

      // if no cart, return
      if (!currentCart) {
        return false;
      }

      const { supplierUUID, restaurantUUID, supplierTimezone } = currentCart;

      // transform cart items into save cart input
      const setCartInput = salesPortalCartInputTransformer({
        cartItems: currentCart.cartItems,
        deliveryDate: selectedFulfillment.date,
        orderUUID: currentCart.orderUUID,
        restaurantUUID,
        selectedFulfillment,
        supplierUUID,
        updatedAt: currentCart.lastUpdatedAt,
      });

      try {
        // save fulfillment
        const { data } = await saveSalesPortalCartMutation({
          variables: { cart: setCartInput },
        });

        // if active restaurant uuid differes from the provided restaurant uuid;
        // prevent corrupting saved cart
        if (
          useCartStore.getState().getCart()?.restaurantUUID !== restaurantUUID
        ) {
          return false;
        }

        // in-memory cart might have changed since we made the request
        const currentLocalUpdatedAt = Date.parse(
          useCartStore.getState().getCart()?.lastUpdatedAt ?? '',
        );
        const lastRemoteUpdatedAt = Date.parse(
          data?.saveSalesPortalCart?.cart?.metadata?.updated_at ?? '',
        );

        // if save was successful, sync local with remote
        if (data?.saveSalesPortalCart.success) {
          // if remote cart is newer, sync local with remote
          if (lastRemoteUpdatedAt >= currentLocalUpdatedAt) {
            syncSavedCartWithRemoteCart(
              restaurantUUID,
              data.saveSalesPortalCart.cart,
              selectedFulfillment,
              supplierTimezone,
              supplierUUID,
            );
          }
          return true;
        } else if (data?.saveSalesPortalCart.error) {
          syncSavedCartWithRemoteCart(
            restaurantUUID,
            data.saveSalesPortalCart.cart,
            currentCart.fulfillment,
            supplierTimezone,
            supplierUUID,
          );
          throw new SaveSalesPortalCartError({
            code: data.saveSalesPortalCart.error.code,
            message: data.saveSalesPortalCart.error.message,
          });
        } else {
          throw new SaveSalesPortalCartError({
            code: CartUpdateError.UnexpectedException,
            message: data
              ? 'saveSalesPortalCart returned neither success nor error'
              : 'saveSalesPortalCart returned a null payload',
          });
        }
      } catch (error) {
        captureException(error, {
          extra: {
            mutation: 'saveSalesPortalCartFulfillment',
            orderUUID: currentCart.orderUUID,
            restaurantUUID,
            supplierUUID,
          },
        });
        showErrorToast(
          i18n.t('sales_portal.order_entry.save_cart_error.fulfillment'),
        );
        dropFailedOrderUUID(currentCart.orderUUID, restaurantUUID);
        return false;
      }
    },
    [saveSalesPortalCartMutation, showErrorToast],
  );
};

/**
 * Mutator to save and validate the in-memory cart.
 * Reads cart metadata (supplierUUID, restaurantUUID, supplierTimezone) from the cart store.
 * Errors are handled internally and displayed via toast.
 *
 * @returns {Promise<boolean>} true if save succeeded, false if it failed
 *
 * @example
 * ```typescript
 * const { saveAndValidateCart } = useSaveAndValidateSalesPortalCartMutator();
 * const success = await saveAndValidateCart();
 * if (success) {
 *   // proceed with next steps
 * }
 * ```
 */
export const useSaveAndValidateSalesPortalCartMutator = () => {
  const [saveAndValidateSalesPortalCartMutation, { loading, data, error }] =
    useSaveAndValidateSalesPortalCartMutation({
      context: { noBatch: true },
      fetchPolicy: 'no-cache',
    });
  const { showErrorToast } = useExperienceToasts();

  const saveAndValidateCart = useCallback(async (): Promise<boolean> => {
    // get current in-memory cart
    const currentCart = useCartStore.getState().getCart();

    if (!currentCart) {
      showErrorToast(
        i18n.t('sales_portal.order_entry.save_cart_error.missing_cart'),
      );
      return false;
    }

    const {
      supplierUUID,
      restaurantUUID,
      supplierTimezone,
      fulfillment: selectedFulfillment,
    } = currentCart;

    if (!selectedFulfillment) {
      showErrorToast(
        i18n.t('sales_portal.order_entry.save_cart_error.missing_fulfillment'),
      );
      return false;
    }

    const lastUpdatedAt = currentCart.lastUpdatedAt ?? new Date().toISOString(); // default to current timestamp
    const cartItems = currentCart.cartItems ?? [];
    const orderUUID = currentCart.orderUUID ?? null;

    // transform cart items into save cart input
    const setCartInput = salesPortalCartInputTransformer({
      cartItems,
      deliveryDate: selectedFulfillment.date,
      orderUUID,
      restaurantUUID,
      selectedFulfillment,
      supplierUUID,
      updatedAt: lastUpdatedAt,
    });

    try {
      // save cart
      const { data } = await saveAndValidateSalesPortalCartMutation({
        variables: { cart: setCartInput },
      });

      handleSaveSalesPortalCartResponse(
        data?.saveSalesPortalCart ?? null,
        restaurantUUID,
        selectedFulfillment,
        supplierTimezone,
        supplierUUID,
      );
      return true;
    } catch (error) {
      captureException(error, {
        extra: {
          mutation: 'saveAndValidateSalesPortalCart',
          orderUUID,
          restaurantUUID,
          supplierUUID,
        },
      });
      showErrorToast(
        i18n.t('sales_portal.order_entry.save_cart_error.unknown_error'),
      );
      dropFailedOrderUUID(orderUUID, restaurantUUID);
      return false;
    }
  }, [saveAndValidateSalesPortalCartMutation, showErrorToast]);

  const orderValidation = useMemo(() => {
    return data?.saveSalesPortalCart.order_validation
      ? orderValidationTransformer(data.saveSalesPortalCart.order_validation)
      : null;
  }, [data?.saveSalesPortalCart.order_validation]);

  return {
    error,
    isSavingAndValidatingCart: loading,
    orderValidation,
    saveAndValidateCart,
  };
};
